import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Star } from "lucide-react";
import { MaskLines } from "./Reveal";
import { useLang, IMG, WHATSAPP, VCALENDAR } from "../lib/i18n";
import { scrollToId } from "./Nav";

const ease = [0.22, 1, 0.36, 1];

const RatingSeal = ({ label }) => {
  const chars = `${label.toUpperCase()} · GIOIA · `.split("");
  return (
    <div className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-[#14110F]/85 backdrop-blur-md border border-[#C9A66B]/30" data-testid="hero-rating-seal">
      <div className="absolute inset-0 spin-slow" aria-hidden="true">
        {chars.map((c, i) => (
          <span
            key={i}
            className="absolute left-1/2 top-[6%] h-[44%] origin-bottom -translate-x-1/2 text-[8px] sm:text-[9.5px] font-medium text-[#F6F2EC]"
            style={{ transform: `translateX(-50%) rotate(${(i * 360) / chars.length}deg)` }}
          >
            {c}
          </span>
        ))}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-serif-d text-4xl sm:text-5xl text-[#F6F2EC] leading-none">4,8</span>
        <span className="flex gap-0.5 mt-1">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-2.5 h-2.5 fill-[#C9A66B] text-[#C9A66B]" />)}</span>
      </div>
    </div>
  );
};

const HeroFrame = ({ label }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1.24]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={onLeave}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 0 0)" }}
          transition={{ duration: 1.6, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
          className="relative arch overflow-hidden aspect-[3/4] w-full bg-[#1e1a17]"
          data-testid="hero-image-frame"
        >
          <motion.img style={{ y, scale }} src={IMG.hero} alt="Gioia Anfosso, fondatrice di Gioia Centro Estetico" className="absolute inset-0 w-full h-full object-cover object-[50%_25%]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,transparent_30%,rgba(20,17,15,0.55)_100%)]" />
        </motion.div>
        <div className="absolute -inset-3 sm:-inset-4 arch border border-[#C9A66B]/40 pointer-events-none" style={{ transform: "translateZ(40px)" }} />
        <div className="absolute -left-6 sm:-left-14 bottom-10" style={{ transform: "translateZ(80px)" }}>
          <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.6, duration: 1, ease }}>
            <RatingSeal label={label} />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  return (
    <section id="top" data-testid="hero-section" className="relative bg-[#14110F] text-[#F6F2EC] overflow-hidden grain">
      <div className="absolute top-0 right-0 w-[60vw] h-[60vw] rounded-full bg-[#C9A66B]/10 blur-[120px] pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-10 pt-44 sm:pt-44 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-12 lg:gap-8 items-end min-h-[100svh]">
        <div className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="overline text-[#C9A66B] mb-8 flex items-center gap-4">
            <span className="w-10 h-px bg-[#C9A66B]" /> {h.over}
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif-d font-light leading-[0.92] tracking-[-0.02em] text-[3.4rem] sm:text-7xl lg:text-[7.4rem]">
            <MaskLines key={h.lines.join()} lines={h.lines.slice(0, 2)} delay={0.35} />
            <MaskLines key={h.lines[2]} lines={[h.lines[2]]} delay={0.6} lineClass="italic text-[#C9A66B]" />
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease }} className="mt-10 max-w-xl">
            <p className="text-[#d9d0c4] text-base leading-relaxed font-light" data-testid="hero-subtitle">{h.sub}</p>
            <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-x-8 gap-y-5 sm:items-center">
              <button
                data-testid="hero-book-btn"
                onClick={() => scrollToId("booking")}
                className="group inline-flex items-center justify-between gap-6 whitespace-nowrap rounded-full bg-[#C9A66B] text-[#14110F] pl-7 pr-2 py-2 text-[0.75rem] tracking-[0.2em] uppercase font-medium transition-colors duration-500 hover:bg-[#F6F2EC]"
              >
                {h.cta}
                <span className="w-10 h-10 rounded-full bg-[#14110F] text-[#F6F2EC] flex items-center justify-center transition-transform duration-500 group-hover:rotate-[-45deg]">
                  <ArrowDownRight className="w-4 h-4" />
                </span>
              </button>
              <a
                data-testid="hero-whatsapp-link"
                href={WHATSAPP}
                target="_blank"
                rel="noreferrer"
                className="self-start sm:self-auto whitespace-nowrap text-[0.75rem] tracking-[0.2em] uppercase text-[#F6F2EC]/85 hover:text-[#C9A66B] transition-colors duration-300 underline underline-offset-8 decoration-[#C9A66B]/50"
              >
                {h.wa}
              </a>
              <a
                data-testid="hero-vcalendar-link"
                href={VCALENDAR}
                target="_blank"
                rel="noreferrer"
                className="self-start sm:self-auto whitespace-nowrap text-[0.75rem] tracking-[0.2em] uppercase text-[#F6F2EC]/85 hover:text-[#C9A66B] transition-colors duration-300 underline underline-offset-8 decoration-[#C9A66B]/50"
              >
                {t.booking.vcal}
              </a>
            </div>
            <p className="mt-10 overline text-[#b8ad9f] flex items-center gap-3" data-testid="hero-open-status">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9A66B] animate-pulse" /> {h.open} · {h.rating}
            </p>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative px-6 sm:px-16 lg:px-0 lg:pl-10">
          <HeroFrame label={h.rating} />
        </div>
      </div>
    </section>
  );
};
